import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { FilterConfig, GlobalFilterPanelComponent } from '../components/global-filter-panel/global-filter-panel.component';
import { DashboardService } from '../dashboard.service';
import { DashboardEngineService } from '../services/dashboard-engine.service';
import { ActivityTimelineComponent } from '../widgets/shared/activity-timeline/activity-timeline.component';
import { KpiCard, KpiCardGridComponent } from '../widgets/shared/kpi-card-grid/kpi-card-grid.component';
import { SurveyOverviewComponent } from '../widgets/shared/survey-overview/survey-overview.component';

const TYPE_BADGE_CLASS: Record<string, string> = {
  inquiry: 'text-blue-700 bg-blue-50',
  complaint: 'text-red-700 bg-red-50',
  suggestion: 'text-green-700 bg-green-50',
};

@Component({
  selector: 'app-support-dashboard',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    NgClass,
    DatePipe,
    RouterLink,
    GlobalFilterPanelComponent,
    KpiCardGridComponent,
    ActivityTimelineComponent,
    SurveyOverviewComponent,
  ],
  templateUrl: './support-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: block; width: 100%;' },
})
export class SupportDashboardComponent {
  private engine = inject(DashboardEngineService);
  protected dashboardSvc = inject(DashboardService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private auth = inject(AuthService);

  readonly dashboardType = 'support';
  filters: any = {};

  get hasTicketPermission(): boolean {
    return this.auth.canAccess(['happiness_center_view']);
  }

  ticketData: any = null;
  categoryData: any = null;
  openData: any = null;

  loadingTicket = false;
  loadingCategory = false;
  loadingOpen = false;

  ticketKpis: KpiCard[] = [];

  readonly filterConfig: FilterConfig = {
    community: true,
    period: true,
    dateRange: false,
    status: false,
    priority: false,
  };

  onFiltersChanged(f: any): void {
    this.filters = { ...f };
    this.dashboardSvc.activeFilters.set(this.filters);
    this.loadAll();
    this.cdr.markForCheck();
  }

  private loadAll(): void {
    this.loadTicketSummary();
    this.loadCategoryBreakdown();
    this.loadOpenTickets();
  }

  private loadTicketSummary(): void {
    this.loadingTicket = true;
    this.engine
      .getWidgetData(this.dashboardType, 'ticketSummary', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const d = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.ticketData = d;
          this.ticketKpis = [
            {
              label: 'Total Tickets',
              value: d.total ?? 0,
              icon: 'confirmation_number',
              color: 'text-blue-600',
              bgColor: 'bg-blue-50',
              subtitle: 'All Happiness Center tickets (inquiries, complaints, suggestions) created within the selected date range.',
            },
            {
              label: 'Open',
              value: d.open ?? 0,
              icon: 'inbox',
              color: 'text-indigo-600',
              bgColor: 'bg-indigo-50',
              subtitle: 'Tickets with status Open or Under Process — created within the selected date range and not yet resolved.',
            },
            {
              label: 'Pending',
              value: d.pending ?? 0,
              icon: 'pending_actions',
              color: 'text-yellow-600',
              bgColor: 'bg-yellow-50',
              subtitle: 'Tickets currently marked Under Process — being actively worked on by staff.',
            },
            {
              label: 'Closed',
              value: d.closed ?? 0,
              icon: 'task_alt',
              color: 'text-green-600',
              bgColor: 'bg-green-50',
              subtitle: 'Tickets with status Resolved within the selected date range.',
            },
            {
              label: 'Overdue',
              value: d.overdue ?? 0,
              icon: 'alarm',
              color: 'text-red-600',
              bgColor: 'bg-red-50',
              subtitle: 'Open or Under Process tickets that have been unresolved for more than 3 days since creation.',
            },
          ];
          this.loadingTicket = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingTicket = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadCategoryBreakdown(): void {
    if (!this.hasTicketPermission) return;
    this.loadingCategory = true;
    this.engine
      .getWidgetData(this.dashboardType, 'categoryBreakdown', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.categoryData = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.loadingCategory = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingCategory = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadOpenTickets(): void {
    if (!this.hasTicketPermission) return;
    this.loadingOpen = true;
    this.engine
      .getWidgetData(this.dashboardType, 'openTickets', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.openData = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.loadingOpen = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingOpen = false;
          this.cdr.markForCheck();
        },
      });
  }

  getStatusBadgeClass(status: string): string {
    const s = status?.toLowerCase();
    if (s === 'open') return 'bg-blue-100 text-blue-700';
    if (s === 'pending' || s === 'under process' || s === 'in-process') return 'bg-amber-100 text-amber-700';
    if (s === 'closed' || s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'hold') return 'bg-orange-100 text-orange-700';
    return 'bg-gray-100 text-gray-500';
  }

  getTypeBadgeClass(type: string): string {
    return TYPE_BADGE_CLASS[type?.toLowerCase()] ?? 'text-gray-700 bg-gray-50';
  }

  getTypeTotal(): number {
    return (this.categoryData?.by_type ?? []).reduce((s: number, t: any) => s + (t.count ?? 0), 0);
  }

  getTypePercent(count: number): number {
    const total = this.getTypeTotal();
    return total > 0 ? Math.round((count / total) * 100) : 0;
  }
}
